import React from 'react';
import {
  IonModal,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
} from '@ionic/react';
import SectionCard from '@/components/SectionCard';
import styles from './MessagePreviewSheet.module.css';
import {
  PREVIEW_SHEET_BREAKPOINT,
  PREVIEW_SHEET_BREAKPOINTS,
} from '@/modules/config';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import type { MessageIntent } from '@/modules/template';

/**
 * A hypothetical reshare of the message being composed (#447 follow-up): what
 * the next sharing leg would send if one of these recipients passed this on.
 *
 * The sender is about to set something travelling, and the compose step only
 * shows them their own end of it — their message, addressed to the people they
 * picked, with the fields still shown as tokens to fill. This shows the other
 * end: the same template and the same field values, hydrated against a fake
 * recipient rather than one of theirs, so it reads as a message to somebody
 * they have never met.
 *
 * It is a degree further on, not the same message re-addressed: it composes
 * from the reshare body whatever mode this send is in, its story carries this
 * sender as the newest ancestor, and its reach counts the people being written
 * to now. See nextDegreeTokenArgs in ShareWizardContext.
 *
 * A read-only detour rather than a step — it edits nothing, and the compose step
 * stays behind it, so the sender can look and go straight back. Nothing is
 * fetched: the message is already composed.
 */
const MessagePreviewSheet: React.FC<{
  isOpen: boolean;
  /** The compose step this was opened from — which message to pass on. */
  intent: MessageIntent;
  onDismiss: () => void;
}> = ({ isOpen, intent, onDismiss }) => {
  const { getHypotheticalPreviewForIntent, cycleHypotheticalPeople } =
    useShareWizardContext();

  // New names on the way out, not on the way in: the names are made up, and a
  // different pair each time the sheet is opened is what says so. Doing it as
  // it closes keeps them still while the sender is reading. Hung on
  // onDidDismiss alone, which both the Done button and a drag-down reach, so
  // the names change once per look either way.
  const handleDidDismiss = () => {
    cycleHypotheticalPeople();
    onDismiss();
  };

  return (
    <IonModal
      isOpen={isOpen}
      initialBreakpoint={PREVIEW_SHEET_BREAKPOINT}
      breakpoints={PREVIEW_SHEET_BREAKPOINTS}
      // Scroll the message at two thirds rather than making the sender drag the
      // sheet to full height first: there is nothing under the preview to reach
      // by expanding, and the compose step behind it is the reason the sheet
      // stops short of the top.
      expandToScroll={false}
      handle
      onDidDismiss={handleDidDismiss}
      data-testid="message-preview-sheet"
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Reshare Preview</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={onDismiss}
              data-testid="message-preview-sheet-close"
            >
              Done
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <SectionCard className="section-card--message-preview" noBottomMargin>
          <div
            className={`section-card__message-preview ${styles.preview}`}
            data-testid="message-preview-sheet-content"
          >
            {getHypotheticalPreviewForIntent(intent)}
          </div>
        </SectionCard>
      </IonContent>
    </IonModal>
  );
};

export default MessagePreviewSheet;
